'use client';

import { lazy, Suspense, useState } from 'react';
import { Link } from '@/i18n/navigation';
import { showWalletModal } from '@/features/wallet/components/wallet-modal';
import { showLoginModal } from '@/features/auth/components/login-form';
import { useAtomValue } from 'jotai';
import { isLoginAtom } from '@/store';
import { useTranslations } from 'next-intl';
import { menuTabUrl } from '@/const/page-url';
import { isInApp } from '@/libs/comeAppSDK';
import { useContactCustomerService } from "@/components/contact-customer-service/use-contact-customer-service";
import IconFont from '@/components/ui/iconFont';
import DepositFaq from '@/components/layout/deposit-faq';
import { usePathname, useRouter } from 'next/navigation';

const AppInfoLink = lazy(() => import('@/features/home/components/fag-list/app-info'));
export interface FaqListProps {
  itemClz?: string;
}

export default function FaqList({ itemClz = '' }: FaqListProps) {
  const { handleContact } = useContactCustomerService({ source: 'Footer' });
  const t = useTranslations('footer');
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set([3]));
  const isLogin = useAtomValue(isLoginAtom);
  const router = useRouter();
  const pathname = usePathname();
  const locale = pathname.split('/')[1];

  const toggleExpanded = (index: number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleRecharge = () => {
    if (!isLogin) {
      showLoginModal();
      return;
    }
    showWalletModal();
  };

  const handleJump2ResponsibleGaming = () => {
    router.push(`/${locale}${process.env.NEXT_PUBLIC_RESPONSIBLE_GAMING_URL}`);
  }

  const features = [
    {
      title: 'Casino',
      href: menuTabUrl.casino,
    },
    {
      title: 'Rummy',
      href: menuTabUrl.rummy,
    },
    {
      title: 'Fantasy',
      href: menuTabUrl.fantasy,
    },
    {
      title: 'Sports',
      href: menuTabUrl.sports,
    },
    {
      title: t('refer'),
      href: '/affiliate',
    },
    {
      title: t('vip'),
      href: '/vip',
    },
    {
      title: t('promotions'),
      href: '/promotions',
    },
    {
      title: t('deposit'),
    },
  ];

  const helps = [
    {
      title: t('privacy'),
      href: '/agreement/privacy',
    },
    {
      title: t('terms'),
      href: '/agreement/user',
    },
    {
      title: t('customer'),
      href: '',
      handle: handleContact
    },
    {
      title: t('responsible_gaming'),
      href: '',
      handle: handleJump2ResponsibleGaming
    },
  ];

  const list = [
    {
      title: t('feature'),
      description: <div className="flex flex-col flex-wrap items-start gap-[25px] leading-[10px] font-medium text-tertiary text-ss">
        {
          features.map((item, index) => {
            if (!item.href) {
              return (
                <span key={index} onClick={() => handleRecharge()} className="text-tertiary text-ss font-medium origin-center active:scale-90 leading-[10px]">{item.title}</span>
              );
            }
            return (
              <Link key={index} href={item.href} className="text-tertiary origin-center active:scale-90">{item.title}</Link>
            );
          })
        }
        {!isInApp() && <Suspense>
          <AppInfoLink />
        </Suspense>}
      </div>,
    },
    ...(process.env.NEXT_PUBLIC_BRAND === 'come' ? [{
      title: t('contact'),
      description: <div className="flex flex-col flex-wrap items-start gap-[25px] leading-[10px]">
        <div className="flex flex-col gap-[10px]">
          <Link href="mailto:support@comevip.com" className="text-accent-blue text-ss font-medium active:scale-90 leading-[10px]">support@comevip.com</Link>
          <div className="text-tertiary text-ss font-medium">{t('email1')}</div>
        </div>
        <div className="flex flex-col gap-[10px]">
          <Link href="mailto:business@comevip.com" className="text-accent-blue text-ss font-medium active:scale-90 leading-[10px]">business@comevip.com</Link>
          <div className="text-tertiary text-ss font-medium">{t('email2')}</div>
        </div>
      </div>,
    },] : []),
    {
      title: t('help'),
      description: <div className="flex flex-col flex-wrap items-start gap-[25px] leading-[10px]">
        {
          helps.map((item, index) => {
            if (item.handle) {
              return (
                <span
                  key={index}
                  onClick={(e) => {
                    e.preventDefault();
                    item.handle();
                  }}
                  className="text-tertiary text-ss font-medium origin-center active:scale-90 leading-[10px] cursor-pointer"
                >
                  {item.title}
                </span>
              );
            }
            if (item.href) {
              return (
                <Link key={index} href={item.href} className="text-tertiary text-ss font-medium origin-center active:scale-90">{item.title}</Link>
              );
            }
            return (
              <span className='text-tertiary text-ss font-medium active:scale-90' key={index}>{item.title}</span>
            );
          })
        }
      </div>,
    },
    // {
    //   title: '',
    //   description: <div className="mt-[-24px]">
    //     <div className="flex justify-between items-center">
    //       <div className="w-[24px] h-[24px] ml-[5px] border border-tertiary rounded-full text-tertiary leading-[10px] text-xxs font-semibold flex items-center justify-center">18+</div>
    //       <Image src={footerTitleImg} alt="logo" width={91} height={16} />
    //       <Image src={gcbImg} alt="logo" width={60} height={24} />
    //     </div>
    //     <div className="text-tertiary text-xss leading-[15px] mt-[15px] font-medium">
    //       {t('marketing')}
    //     </div>
    //   </div>,
    // },
  ];
  return (
    <div>
      {
        list.map((item, index) => {
          const isExpanded = expandedItems.has(index);
          return (
            <div key={index} className={`flex flex-col pt-[19.5px] px-[12px] ${itemClz}`}>
              <div className="mb-[23px]">
                {
                  item.title && (
                    <>
                      <div
                        className="flex justify-between items-center w-full cursor-pointer"
                        onClick={() => toggleExpanded(index)}
                      >
                        <span className="text-secondary text-xs font-medium leading-[10px]">{item.title}</span>
                        <IconFont name="arrow-up" size={10} color="#a9b5cb" className={`mt-[4px] mr-[15px] transition-transform duration-200 ${!isExpanded ? 'rotate-180' : ''
                          }`} />
                      </div>
                    </>
                  )
                }
                {isExpanded && (
                  <div className="text-tertiary text-xss leading-[10px] mt-[24px] font-medium">
                    {item.description}
                  </div>
                )}
              </div>
              <div className="bg-[rgba(214,234,255,0.1)] w-full h-[1px] px-[12px]" />
            </div>
          );
        })
      }
      <DepositFaq />
    </div>
  );
}